import axios from 'axios';
import { uid } from 'uid';

const removeUndefinedValues = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues); // Recursively process each item in the array
  } else if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, value]) => value !== undefined) // Remove entries with undefined values
        .map(([key, value]) => [key, removeUndefinedValues(value)]) // Recursively process each value
    );
  }
  return obj; // Return the value if it's not an array or object
};

export const formService = {
  async getForms() {
    try {
      const response = await axios.get('/forms');
      return response.data;
    } catch (error) {
      console.error('Error fetching forms:', error);
      throw error;
    }
  },

  async getFormById(id) {
    try {
      const response = await axios.get(`/forms/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching forms:', error);
      throw error;
    }
  },

  async updateForm(id, form) {
    try {
      const sanitizedData = removeUndefinedValues(form);
      const response = await axios.patch(`/forms/${id}`, sanitizedData);
      return response.data;
    } catch (error) {
      console.error('Error updating form:', error);
      throw error;
    }
  },

  async publishForm(id) {
    try {
      const response = await axios.post(`/forms/${id}/publish`);
      return response.data;
    } catch (error) {
      console.error('Error updating form:', error);
      throw error;
    }
  },

  async unpublishForm(id) {
    try {
      const response = await axios.post(`/forms/${id}/unpublish`);
      return response.data;
    } catch (error) {
      console.error('Error updating form:', error);
      throw error;
    }
  },

  async deleteForm(id) {
    try {
      const response = await axios.delete(`/forms/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting form:', error);
      throw error;
    }
  },

  async getAllSubmissions(params = {}) {
    try {
      const response = await axios.get('/submissions', {
        params: {
          formId: params.formId || null,
          page: params.page || 1,
          limit: params.limit || 50,
          status: params.status || null,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching all submissions:', error);
      throw error;
    }
  },

  /**
   * Get a single submission by ID
   * @param {string} submissionId - The submission ID
   * @returns {Promise<Object>} The submission data
   */
  async getSubmission(submissionId) {
    try {
      const response = await axios.get(`/submissions/${submissionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching submission:', error);
      throw error;
    }
  },

  /**
   * Get submission statistics
   * @param {Object} params - Query parameters
   * @param {string} params.period - Period filter (30d, 90d, ytd, all)
   * @param {string} params.formId - Optional form ID to filter by
   * @returns {Promise<Object>} Statistics data with current, previous, and trends
   */
  async getSubmissionStats(params = {}) {
    try {
      const response = await axios.get('/submissions/stats', {
        params: {
          period: params.period || '30d',
          formId: params.formId || null,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching submission stats:', error);
      throw error;
    }
  },

  async exportSubmissions(id) {
    try {
      const response = await axios.get(`/forms/${id}/submissions/export`);
      return response.data;
    } catch (error) {
      console.error('Error exporting submissions:', error);
      throw error;
    }
  },

  async createForm(data) {
    try {
      // Default pages for regular forms
      const defaultPages = [
        {
          id: uid(),
          title: 'Page 1',
          elements: [],
        },
      ];

      // Default ending pages
      const defaultEndingPages = [
        {
          id: uid(),
          title: 'Ending',
          elements: [
            {
              id: uid(),
              type: 'thank-you',
              label: 'Thank You',
              props: {
                icon: 'check-circle',
                label: 'Thank You',
                message: 'Thank you for your submission!',
                iconSize: '48',
                conditions: {
                  type: 'show',
                  groups: [],
                  betweenGroupLogic: 'AND',
                },
              },
            },
          ],
        },
      ];

      const response = await axios.post('/forms', {
        name: data.flow_title,
        description: data.description || '',
        type: data.type || 'default',
        pages: data.pages || defaultPages,
        ending_pages: data.ending_pages || defaultEndingPages,
        settings: data.settings || {},
        designId: data.designId || null,
        uid: data.uid,
      });
      return response;
    } catch (error) {
      console.error('Error creating form:', error);
      throw error;
    }
  },

  async createFormWithAI(data) {
    try {
      const response = await axios.post('/ai/generate-form', {
        title: data.flow_title,
        description: data.description,
        organizationId: data.organizationId,
        uid: data.uid,
      });
      return response;
    } catch (error) {
      console.error('Error creating form with AI:', error);
      throw error;
    }
  },
};
